import React from "react";

import { Column, Table } from "@tanstack/react-table";
import { MenuProps } from "antd";

import { FilterTarget } from "../../../domain/table/filterValueWithMode";
import { FilterBar, useFilterBarCollapse } from "../../FilterBar";
import { columnPlainLabel } from "../TSTTable/utils/columnPlainLabel";

import * as theme from "./TSTFilterBar.css";
import { ModeConfig, TSTFilterBarRow } from "./TSTFilterBarRow";
import { useTSTFilterUtils } from "./useTSTFilterUtils";

export interface TSTFilterBarProps<T> {
    table: Table<T>;
    addLabel: string;
    globalSearchLabel: string;
    hideFiltersLabel: string;
    clearAllLabel: string;
    getFilterCountLabel: (filterCount: number) => string;
    rowCountLabel?: string;
    /** The set-mode value editor for the column's main value or its exception. */
    getFilterComponent: (
        column: Column<T>,
        target: FilterTarget
    ) => React.ReactNode;
    /** Mode config applied to all columns. Use getModeConfig for per-column control. */
    modeConfig?: ModeConfig;
    /** Returns mode config per column. Overrides modeConfig if provided. */
    getModeConfig?: (column: Column<T>) => ModeConfig | undefined;
    /**
     * Columns whose filter can take an exception: a second value matched with
     * the opposite operator. Needs `addExceptionLabel`.
     */
    canAddException?: (column: Column<T>) => boolean;
    addExceptionLabel?: string;
    /** Storage key used to persist the collapsed/expanded state. Omit to keep it ephemeral. */
    tableId?: string;
    /** Initial collapsed state used until the user changes it. Only applies with tableId. */
    defaultCollapsed?: boolean;
    /** Always-visible filters rendered above the collapsible filter rows. */
    aboveFilters?: React.ReactNode;
    /** Always-visible filters rendered below the filter rows. */
    belowFilters?: React.ReactNode;
    /** Number of active always-visible custom filters to fold into the count. */
    extraFilterCount?: number;
    /**
     * Overrides the "clear all" handler. Tables with data params should pass
     * `clearAllFilters` from usePresetTableSearchParams so column and data
     * params clear in one navigation. Defaults to resetting column filters.
     */
    onClearFilters?: () => void;
}
export function TSTFilterBar<T>({
    table,
    addLabel,
    globalSearchLabel,
    getFilterCountLabel,
    rowCountLabel,
    hideFiltersLabel,
    clearAllLabel,
    getFilterComponent,
    modeConfig,
    getModeConfig,
    canAddException,
    addExceptionLabel,
    tableId,
    defaultCollapsed,
    aboveFilters,
    belowFilters,
    extraFilterCount,
    onClearFilters,
}: TSTFilterBarProps<T>) {
    const {
        selectedFilterColumns,
        filterableColumns,
        selectedFilterColumnIds,
    } = useTSTFilterUtils<T>(table);

    const handleAddFilter = (columnId: string | null) => {
        if (!columnId) {
            return;
        }
        table.getColumn(columnId)?.setFilterValue(null);
    };
    const addFilterItems: MenuProps["items"] = filterableColumns
        .filter((col) => !selectedFilterColumnIds.includes(col.id))
        .map((col) => ({
            label: columnPlainLabel(col, table),
            key: col.id,
            onClick: () => handleAddFilter(col.id),
        }));

    const filterCount = selectedFilterColumnIds.length;

    const [collapsed, setCollapsed] = useFilterBarCollapse(
        tableId ?? "",
        defaultCollapsed
    );
    const collapseProps = tableId
        ? { collapsed, onCollapsedChange: setCollapsed }
        : {};

    return (
        <div className={theme.mutedFrame}>
            <FilterBar
                {...collapseProps}
                addLabel={addLabel}
                hideFiltersLabel={hideFiltersLabel}
                clearAllLabel={clearAllLabel}
                getFilterCountLabel={getFilterCountLabel}
                addFilterItems={addFilterItems}
                filterCount={filterCount}
                globalSearch={{
                    label: globalSearchLabel,
                    onChange: (value) => table.setGlobalFilter(value),
                    getValue: () => table.getState().globalFilter,
                }}
                onClearFilters={
                    onClearFilters ?? (() => table.resetColumnFilters())
                }
                rowCountLabel={rowCountLabel}
                aboveFilters={aboveFilters}
                belowFilters={belowFilters}
                extraFilterCount={extraFilterCount}
            >
                {selectedFilterColumns.map((col) => {
                    return (
                        <TSTFilterBarRow
                            key={col.id}
                            column={col}
                            table={table}
                            modeConfig={getModeConfig?.(col) ?? modeConfig}
                            exceptionConfig={
                                addExceptionLabel !== undefined &&
                                canAddException?.(col)
                                    ? {
                                          addLabel: addExceptionLabel,
                                          renderComponent: () =>
                                              getFilterComponent(
                                                  col,
                                                  "exception"
                                              ),
                                      }
                                    : undefined
                            }
                        >
                            {getFilterComponent(col, "main")}
                        </TSTFilterBarRow>
                    );
                })}
            </FilterBar>
        </div>
    );
}
